"use client";

import { useEffect, useState } from "react";
import { IconType } from "react-icons";
import { IoSearchOutline } from "react-icons/io5";
import { LuBookOpen, LuLayoutDashboard, LuHouse } from "react-icons/lu";
import { RiPencilRuler2Line } from "react-icons/ri";
import Image from "next/image";
import { LogoPila } from "../assets";
import { FaUserCog } from "react-icons/fa";
import { RiFileUserLine } from "react-icons/ri";
import { IoGameControllerOutline } from "react-icons/io5";
import { BsBook } from "react-icons/bs";
import { useTranslations } from "next-intl";
import {
	USER_GUIDE_DOC_KEYS,
	type UserGuideDocKey,
} from "../constants/userGuideDocs";

export type SidebarLabel =
	| "คลังบทเรียนต้นแบบ"
	| "คลังบทเรียนของคุณ"
	| "สร้างแบบฝึกหัดใหม่"
	| "Dashboard"
	| "จัดการหน้า Home"
	| "ผู้ดูแลระบบ"
	| "Log การใช้งาน"
	| "จัดการเกม"
	| "คู่มือการใช้งาน"

type MenuItem = {
	label: SidebarLabel;
	icon: IconType;
	disabled?: boolean;
};

const menuItemsUser: MenuItem[] = [
	{ label: "คลังบทเรียนต้นแบบ", icon: IoSearchOutline },
	{ label: "คลังบทเรียนของคุณ", icon: LuBookOpen },
	{ label: "สร้างแบบฝึกหัดใหม่", icon: RiPencilRuler2Line },
];

const menuItemsAdmin: MenuItem[] = [
	{ label: "คลังบทเรียนต้นแบบ", icon: IoSearchOutline },
	// { label: "คลังบทเรียนของคุณ", icon: LuBookOpen },
	{ label: "สร้างแบบฝึกหัดใหม่", icon: RiPencilRuler2Line },
	{ label: "Dashboard", icon: LuLayoutDashboard },
	{ label: "จัดการหน้า Home", icon: LuHouse },
	{ label: "ผู้ดูแลระบบ", icon: FaUserCog },
	{ label: "Log การใช้งาน", icon: RiFileUserLine },
	{ label: "จัดการเกม", icon: IoGameControllerOutline },
];

const userGuideMenuItem: MenuItem = {
	label: "คู่มือการใช้งาน",
	icon: BsBook,
};

type SidebarProps = {
	selectedLabel: SidebarLabel;
	selectedUserGuideDoc: UserGuideDocKey | null;
	onSelect: (label: SidebarLabel) => void;
	onSelectUserGuideDoc: (docKey: UserGuideDocKey) => void;
	onCloseMobile?: () => void;
};

const labelToTranslationKey: Partial<Record<SidebarLabel, string>> = {
	"คลังบทเรียนต้นแบบ": "templateLibrary",
	"คลังบทเรียนของคุณ": "myLibrary",
	"สร้างแบบฝึกหัดใหม่": "createExercise",
	"คู่มือการใช้งาน": "userGuide",
	"Dashboard": "dashboard",
	"จัดการหน้า Home": "manageHome",
	"ผู้ดูแลระบบ": "admin",
	"Log การใช้งาน": "logUsage",
	"จัดการเกม": "manageGame",
};

export default function Sidebar({
	selectedLabel,
	selectedUserGuideDoc,
	onSelect,
	onSelectUserGuideDoc,
	onCloseMobile,
}: SidebarProps) {
	const [isAdmin, setIsAdmin] = useState(false);
	const [docOpen, setDocOpen] = useState(false);
	const tMain = useTranslations("main");

	useEffect(() => {
		if (selectedUserGuideDoc) {
			setDocOpen(true);
		}
	}, [selectedUserGuideDoc]);

	useEffect(() => {
		const detectRole = () => {
			try {
				const raw = localStorage.getItem("user");
				if (raw) {
					const parsed = JSON.parse(raw);
					setIsAdmin(parsed?.role === "super_admin" || parsed?.role === "admin");
				} else {
					setIsAdmin(false);
				}
			} catch {
				setIsAdmin(false);
			}
		};

		detectRole();
		window.addEventListener("storage", detectRole);
		window.addEventListener("user-storage-changed", detectRole);
		return () => {
			window.removeEventListener("storage", detectRole);
			window.removeEventListener("user-storage-changed", detectRole);
		};
	}, []);

	const menuItems = isAdmin ? menuItemsAdmin : menuItemsUser;
	const userGuideTranslationKey = labelToTranslationKey[userGuideMenuItem.label];
	const userGuideDisplayLabel = userGuideTranslationKey
		? tMain(userGuideTranslationKey as keyof typeof labelToTranslationKey)
		: userGuideMenuItem.label;
	const isUserGuideActive = docOpen || selectedUserGuideDoc !== null;
	const UserGuideIcon = userGuideMenuItem.icon;

	return (
		<aside className="h-full w-60 shrink-0 overflow-y-auto bg-[#ffffff] p-4 sm:p-5 lg:w-60">
			<nav className="space-y-3">
				{menuItems.map((item) => {
					const Icon = item.icon;
					const isDisabled = Boolean(item.disabled);
					const isActive = selectedLabel === item.label;
					const translationKey = labelToTranslationKey[item.label];
					const displayLabel = translationKey
						? tMain(translationKey as keyof typeof labelToTranslationKey)
						: item.label;

					return (
						<button
							key={item.label}
							type="button"
							disabled={isDisabled}
							onClick={() => {
								setDocOpen(false);
								onSelect(item.label);
								onCloseMobile?.();
							}}
							className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left  text-base transition ${isActive
								? "text-sky-600 cursor-pointer"
								: isDisabled
									? "cursor-not-allowed "
									: "text-[#808080] hover:bg-neutral-200 cursor-pointer"
								}`}
						>
							<Icon className="text-[20px]" />
							<span className="text-base font-normal">{displayLabel}</span>
						</button>
					);
				})}

				<a
					href="https://thailand.pilaproject.org/teacher/classes"
					className="flex w-full items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-neutral-200"
				>
					<Image
						src={LogoPila}
						alt="Pila Workspace"
						width={26}
						height={26}
						className="h-6.5 w-6.5 rounded-md"
					/>
					<span className="text-md font-semibold leading-none text-neutral-700">
						Pila Workspace
					</span>
				</a>

				<div className="my-2 border-t border-[#e5e7eb]" aria-hidden="true" />

				<button
					type="button"
					onClick={() => {
						setDocOpen((prev) => !prev);
						onCloseMobile?.();
					}}
					className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-base transition ${isUserGuideActive
						? "text-sky-600 cursor-pointer"
						: "text-[#808080] hover:bg-neutral-200 cursor-pointer"
						}`}
				>
					<UserGuideIcon className="text-[20px]" />
					<span className="text-base font-normal">{userGuideDisplayLabel}</span>
				</button>

				{docOpen && (
					<ul className="mt-2 ml-8 space-y-1">
						{USER_GUIDE_DOC_KEYS.map((sub) => (
							<li key={sub}>
								<button
									type="button"
									onClick={() => {
										onSelectUserGuideDoc(sub);
										onCloseMobile?.();
									}}
									className={`flex items-center gap-2 text-sm cursor-pointer ${selectedUserGuideDoc === sub ? "font-medium text-sky-600" : "text-[#808080] hover:text-neutral-900"}`}>
									<span className="text-xs">•</span>
									<span>{tMain(sub)}</span>
								</button>
							</li>
						))}
					</ul>
				)}
			</nav>
		</aside>
	);
}
